import { compareAsc } from 'date-fns';
import dayjs from 'dayjs';
import quarterOfYears from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import i18next from 'i18next';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  DepictionDateHeaderOptions_v1 as DateHeaderOptions,
  IDateHeaderSelector,
  ISwimlaneHeaderProps,
  RMFiscalPeriodType as PeriodType,
} from '@/lib/api/types/loop-visualization';

dayjs.extend(quarterOfYears);
dayjs.extend(utc);

const EMPTY_DATE_HEADER_ID = 'no_date';
const PERIOD_SIZES = {
  year: 12,
  half: 6,
  quarter: 3,
  month: 1,
  week: 1,
} as const;

/**
 * Generate date headers for Swimlane based on items dates
 * @param headerSelector
 * @param items
 * @param includeNullHeader
 * @returns Array of ISwimlaneHeaderProps
 */
export function generateDateHeaders(
  headerSelector: IDateHeaderSelector,
  items: ItemV2[],
  includeNullHeader: boolean = false,
): ISwimlaneHeaderProps[] {
  const headerDates = getHeaderDates(headerSelector, items);
  const hasNullDate = includeNullHeader || headerDates.includes(null);
  const validDates = headerDates.filter(Boolean) as string[];
  const sortedDates = validDates.sort(compareAsc);
  const earliestDate = sortedDates[0] || dayjs().format('YYYY-MM-DD');
  const latestDate = sortedDates[sortedDates.length - 1] || dayjs().format('YYYY-MM-DD');
  const periodType = getDateHeaderPeriod(headerSelector.header);
  const headerStartPeriodDate = getPeriodStartDate(earliestDate, periodType);
  const headerEndPeriodDate = getPeriodStartDate(latestDate, periodType);
  const dateHeaderPeriods = getDatesSpanningPeriod(
    headerStartPeriodDate,
    headerEndPeriodDate,
    periodType,
  );
  const headers = dateHeaderPeriods.map((period) => {
    return {
      label: getDateHeaderLabel(periodType, period),
      id: period,
      selector: headerSelector,
      headerColor: '#1c1c1d',
      isDateHeader: true,
    };
  });

  if (hasNullDate) {
    headers.push({
      label: i18next.t(($) => $.loopVisualization.layout.headers.fields.noDate),
      id: EMPTY_DATE_HEADER_ID,
      selector: headerSelector,
      headerColor: '#1c1c1d',
      isDateHeader: true,
    });
  }
  return headers;
}

/**
 * Returns the period of the date header
 * @param header
 * @returns The period (week, month, quarter, half, year)
 */
export function getDateHeaderPeriod(header: DateHeaderOptions): PeriodType {
  if (header.startsWith('startDate')) {
    return header.slice(10) as PeriodType;
  }

  return header.slice(8) as PeriodType;
}

/**
 * Returns the set relevant dates for the given date header and item schedules
 * @param header
 * @param items
 * @returns Array of date strings or nulls
 */
export function getHeaderDates(header: IDateHeaderSelector, items: ItemV2[]): (string | null)[] {
  if (!header || !items) return [];

  let dates: (string | null | undefined)[] = [];
  if (header.type === 'DateSelector' && header.header.startsWith('startDate')) {
    dates = items.map((item) => item.plannedStartDateTime);
  } else {
    dates = items.map((item) => item.plannedFinishDateTime);
  }

  // de-dupe
  const validDates = [...new Set(dates)];
  const datesForHeaders = validDates.map((date) => {
    if (!date) return null;

    return dayjs.utc(date).format('YYYY-MM-DD');
  });

  return datesForHeaders;
}

/**
 * Returns start date of the period containing the passed date
 * @param date
 * @param periodType
 * @returns Start date of period as string
 */
function getPeriodStartDate(date: string, periodType: PeriodType): string {
  if (periodType === 'half') {
    const quarter = dayjs(date).quarter();
    if (quarter === 1 || quarter === 2) {
      return dayjs(date).startOf('year').format('YYYY-MM-DD');
    } else {
      return dayjs(date).quarter(3).startOf('quarter').format('YYYY-MM-DD');
    }
  }

  return dayjs(date).startOf(periodType).format('YYYY-MM-DD');
}

/**
 * Returns an array of dates representing the start of each period spanning from fromDate to toDate
 * @param fromDate
 * @param toDate
 * @param periodType
 * @returns Array of date strings
 */
function getDatesSpanningPeriod(
  fromDate: string,
  toDate: string,
  periodType: PeriodType,
): string[] {
  const intervalType = periodType === 'week' ? 'week' : 'month';
  const stepSize = PERIOD_SIZES[periodType];

  const startDate = dayjs(fromDate);
  const endDate = dayjs(toDate).add(1, 'day'); // add one so that the span's endDate is inclusive

  const spanDates: string[] = [];

  for (let i = 0; startDate.add(i * stepSize, intervalType).valueOf() < endDate.valueOf(); i++) {
    spanDates.push(startDate.add(i * stepSize, intervalType).format('YYYY-MM-DD'));
  }
  return spanDates;
}

function getDateHeaderLabel(periodType: PeriodType, date: string | null): string {
  if (!date) return '';
  const dateObj = dayjs(date);

  switch (periodType) {
    case 'week': {
      const weekStart = dateObj.startOf('week');
      const weekEnd = dateObj.endOf('week');
      if (weekStart.month() === weekEnd.month()) {
        return `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
      }
      return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`;
    }
    case 'month':
      return dayjs(date).format('MMMM YYYY');
    case 'quarter': {
      const quarter = dayjs(date).quarter();
      return `Q${quarter} ${dayjs(date).format('YYYY')}`;
    }
    case 'half': {
      const month = dayjs(date).month(); // 0-indexed
      const half = month < 6 ? 'H1' : 'H2';
      return `${half} ${dayjs(date).format('YYYY')}`;
    }
    case 'year':
      return dayjs(date).format('YYYY');
  }
}

/**
 * Returns the date header ID for an item based on its start and end dates
 * @param headerSelector
 * @param startDate
 * @param endDate
 * @returns header ID as string
 */
export function getItemDateHeaderID(
  headerSelector: IDateHeaderSelector,
  startDate: string | null,
  endDate: string | null,
): string {
  if (!startDate && !endDate) {
    return EMPTY_DATE_HEADER_ID;
  }

  const periodType = getDateHeaderPeriod(headerSelector.header);
  if (headerSelector.header.startsWith('startDate')) {
    return startDate
      ? getPeriodStartDate(dayjs.utc(startDate).format('YYYY-MM-DD'), periodType)
      : EMPTY_DATE_HEADER_ID;
  }
  return endDate
    ? getPeriodStartDate(dayjs.utc(endDate).format('YYYY-MM-DD'), periodType)
    : EMPTY_DATE_HEADER_ID;
}
